import { Link as RouterLink } from 'react-router-dom';
import classNames from 'classnames';

import Button from 'components/partials/button/button';
import Toggle from 'components/partials/toggle/toggle';

import { formatDate } from 'helpers/dates-formatter';

import Icons from 'assets/icons';

export interface ClientProductRow {
  id: string | number;
  name?: string;
  assignedDate?: string;
  [key: string]: unknown;
}

export interface ClientReportRow {
  id: string | number;
  title?: string;
  productId: string | number;
  [key: string]: unknown;
}

interface InfoArg<TValue = unknown> {
  getValue: () => TValue;
  renderValue: () => React.ReactNode;
  row: { original: ClientProductRow };
}

interface RowArg {
  row: { original: ClientProductRow };
}

interface ClientProductsColumnDef {
  id: string;
  accessorKey?: string;
  header: () => React.JSX.Element;
  cell: (info: InfoArg & RowArg) => React.ReactNode;
  headerClassName?: string;
  className?: string;
  enableSorting?: boolean;
  isHeaderCentered?: boolean;
}

const commonStyles = 'pl-4 border-r-2 border-white font-semibold h-12';

export const getClientProductsTableSettings = (
  clientId: string | number | undefined,
  includedReports: ClientReportRow[],
  clientProducts: ClientProductRow[],
  handleToggleChange: (
    product: ClientProductRow,
    newToggleValue: boolean,
  ) => void,
  isInternalAdmin = false,
): ClientProductsColumnDef[] => [
  {
    id: 'name',
    accessorKey: 'name',
    header: () => <span>PRODUCT NAME</span>,
    cell: (info) => <span>{info.renderValue() as React.ReactNode}</span>,
    headerClassName: `${commonStyles} rounded-tl-md rounded-bl-md cursor-pointer`,
    className: 'pl-4 py-3 tablet:w-[20%] font-semibold',
    enableSorting: true,
  },
  {
    id: 'id',
    accessorKey: 'id',
    header: () => <span>INCLUDED REPORTS</span>,
    cell: (info) => {
      const productId = info.getValue() as string | number;

      const reports = includedReports.filter(
        (report) => report.productId === productId,
      );

      if (!reports.length) {
        return null;
      }

      return (
        <span className="w-full text-gray-600">
          {reports.map((report) => report.title).join(', ')}
        </span>
      );
    },
    headerClassName: `${commonStyles} cursor-context-menu`,
    className: 'pl-4 py-3 tablet:w-[40%]',
    enableSorting: false,
  },
  {
    id: 'assignedDate',
    accessorKey: 'assignedDate',
    header: () => <span>ASSIGNED DATE</span>,
    cell: (info) => formatDate(info.row.original.assignedDate, 'MMM D, YYYY'),
    headerClassName: `${commonStyles}`,
    className: 'pl-4 py-3 tablet:w-[15%]',
    enableSorting: false,
  },
  {
    id: 'assigned',
    accessorKey: 'id',
    header: () => <span>ASSIGNMENT</span>,
    cell: ({ row: { original: product } }) => {
      const isAssigned = clientProducts.some((p) => p.id === product.id);

      return (
        <div className="flex justify-center">
          <Toggle
            isToggleOn={isAssigned}
            onToggleChange={(newValue: boolean) =>
              handleToggleChange(product, newValue)
            }
            disabled={isInternalAdmin}
            data-testid="products-tab-table-toggle"
          />
        </div>
      );
    },
    isHeaderCentered: true,
    headerClassName: commonStyles,
    className: 'pl-4 py-3 tablet:w-[12%]',
    enableSorting: false,
  },
  {
    id: 'button-cell',
    accessorKey: 'id',
    header: () => <span></span>,
    cell: (info) => {
      const productId = info.getValue() as string | number;
      const isAssigned = clientProducts.some(
        (product) => product.id === productId,
      );

      return (
        <RouterLink
          to={`/admin/clients/${clientId}/products/${productId}`}
          className={classNames({
            '!pointer-events-none opacity-0': !isAssigned,
          })}
        >
          <Button
            variant="reserve-outlined"
            size="sm"
            iconBefore={<Icons.Eye aria-label="EyeIcon" />}
            className="text-xs md:text-sm"
          >
            View Details
          </Button>
        </RouterLink>
      );
    },
    headerClassName: `${commonStyles} rounded-tr-md rounded-br-md`,
    className: 'py-3 pl-4',
  },
];
